import { ASSURANCE_CHECK_FIELD_ID, ASSURANCE_CHECK_FIELD_ID_TYPE } from './image_metadata_constants';

export const ASSURANCE_MODAL_CONSTANTS: Partial<
  Record<ASSURANCE_CHECK_FIELD_ID_TYPE, { key: string; title: string; description: string; conditionLabel?: string }>
> = {
  [ASSURANCE_CHECK_FIELD_ID.ITEMIZED_MEAL_RECEIPT_CHECK]: {
    key: 'nonItemizedReciepts',
    title: 'Non-itemized Receipt Check',
    description: 'Detects if any given Meal includes an itemized receipt',
    conditionLabel: 'Expenses should have an itemized receipt'
  },
  [ASSURANCE_CHECK_FIELD_ID.FAKE_RECEIPT_DETECTION]: {
    key: 'fakeReceiptDetection',
    title: 'Fake Receipt Detection',
    description: 'Expenses should not contain a fake, altered or AI generated receipt',
    conditionLabel: 'Expenses should not contain fake receipts'
  },
  [ASSURANCE_CHECK_FIELD_ID.ALCOHOL_DETECTION]: {
    key: 'alcoholDetection',
    title: 'Alcohol Detection',
    description: 'Highlight whether a given Meal receipt contains alcohol',
    conditionLabel: 'Alcohol should not exceed the set threshold of the total bill'
  },
  [ASSURANCE_CHECK_FIELD_ID.VENDOR_LEGITIMACY]: {
    key: 'vendorLegitimacyVerification',
    title: 'Vendor Legitimacy Verification',
    description: 'Validate the authenticity of vendors and merchants listed on submitted receipts',
    conditionLabel: 'Vendors listed on receipts should be verified as legitimate'
  },
  [ASSURANCE_CHECK_FIELD_ID.AMOUNT_ANOMALY_DETECTION]: {
    key: 'unusualAmount',
    title: 'Unusual Amount/Price Anomaly',
    description: 'Identify suspiciously high spending based on merchant, item details, and context',
    conditionLabel: 'Expenses should not contain suspiciously high spending'
  },
  [ASSURANCE_CHECK_FIELD_ID.EXCESSIVE_TIP_DETECTION]: {
    key: 'excessiveTipAnomaly',
    title: 'Excessive Tip Anomaly',
    description: 'Highlight whether a given receipt contained Tip that is above a certain threshold',
    conditionLabel: 'Tips should not exceed the set threshold of the total bill'
  },
  [ASSURANCE_CHECK_FIELD_ID.OUTSIDE_BUSINESS_HOURS]: {
    key: 'outsideBusinessHours',
    title: 'Outside Business Hours',
    description: 'Identify expenses that occur outside of normal business hours based on company settings',
    conditionLabel: 'Expenses should not occur outside of business hours'
  }
};

export const ASSURANCE_WARNING_MESSAGES: Record<string, { title: string; content: string }> = {
  [ASSURANCE_CHECK_FIELD_ID.ITEMIZED_MEAL_RECEIPT_CHECK]: {
    title: 'Missing Meal Itemization:',
    content: 'This meal expense may require an itemized receipt. Review the expense and request the detailed receipt if needed.'
  },
  [ASSURANCE_CHECK_FIELD_ID.EXCESSIVE_TIP_DETECTION]: {
    title: 'Excessive Tip:',
    content: 'The tip on this receipt exceeds the configured threshold. Review the tip amount and percentage.'
  },
  [ASSURANCE_CHECK_FIELD_ID.FAKE_RECEIPT_DETECTION]: {
    title: 'Issues with receipt authenticity:',
    content: "This receipt's image content suggests potential manipulation. Review the receipt for authenticity."
  },
  [ASSURANCE_CHECK_FIELD_ID.VENDOR_LEGITIMACY]: {
    title: 'Vendor could not be verified:',
    content:
      'The vendor for this expense could not be confirmed through our verification process. Review the name and location for errors or add missing details.'
  },
  [ASSURANCE_CHECK_FIELD_ID.AMOUNT_ANOMALY_DETECTION]: {
    title: 'Unusual expense amount:',
    content:
      'This expense shows an unusual cost, quantity, or item price compared to typical values. Review for potential errors or out-of-policy spending.'
  },
  [ASSURANCE_CHECK_FIELD_ID.ALCOHOL_DETECTION]: {
    title: 'Meal Alcohol Detected:',
    content: 'Alcohol has been identified on this meal receipt. Review the details for policy compliance.'
  },
  [ASSURANCE_CHECK_FIELD_ID.OUTSIDE_BUSINESS_HOURS]: {
    title: 'Non-Business Day Spend:',
    content: "This expense occurred on a weekend or holiday. Review for compliance with your company's off-hours spending policy."
  },
  [ASSURANCE_CHECK_FIELD_ID.LINE_ITEM_MISMATCH]: {
    title: "Expense and receipt details don't match:",
    content: "This expense has differences from the receipt data. See which fields don't match."
  }
};

/**
 * Push notification configuration for each assurance check type.
 */
export const ASSURANCE_PUSH_NOTIFICATION_CONFIG: Record<string, { title: string; message: string }> = {
  [ASSURANCE_CHECK_FIELD_ID.ITEMIZED_MEAL_RECEIPT_CHECK]: {
    title: 'Itemized receipt required',
    message: 'Your company requires itemized receipts for meal expenses. Please upload an itemized receipt.'
  }
};
